import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Printer, ArrowLeft, FolderKanban, Save } from "lucide-react";
import axios from "axios";
import { STATE_NAMES, forState } from "@/pages/TroMotion";
import { Motion, Ctl, dash, money, uccFor, fmtDate, execLine, borrowerSigOf, acctOf } from "@/pages/CollateralMotion";
import { FIRM } from "@/lib/firm";
import CourtEfaxButton from "@/components/CourtEfaxButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useQuery = () => new URLSearchParams(useLocation().search);
const todayISO = () => new Date().toISOString().slice(0, 10);
const CursiveSig = ({ name }) => name
  ? <span className="lg-sig-cursive" style={{ fontSize: "22pt", lineHeight: 1 }}>{name.startsWith("/s/") ? name : `/s/ ${name}`}</span>
  : <span>______________________________</span>;

const ctx = (app, shared) => {
  const isAuto = (app.debt_type || "mortgage") === "auto";
  const law = forState(shared.state);
  return {
    isAuto,
    law,
    collateralNoun: isAuto ? "vehicle" : "real property",
    CollateralNoun: isAuto ? "Vehicle" : "Real Property",
    enforceVerbs: isAuto
      ? "repossessing, removing, selling, transferring, disposing of, or otherwise exercising enforcement remedies against"
      : "foreclosing upon, conducting or noticing any foreclosure or trustee's sale of, selling, transferring, disposing of, or otherwise exercising enforcement remedies against",
    borrower: app.borrower_full_name || app.borrower_name || "",
    lender: app.current_lender || "",
    bal: app.current_balance,
    propertyAddr: app.property_or_collateral || "",
  };
};

// ── Doc 1: Civil Cover Sheet ─────────────────────────────────────────────
function CivilCoverSheet({ app, shared }) {
  const c = ctx(app, shared);
  const vehLine = [app.vehicle_year, app.vehicle_make, app.vehicle_model].filter(Boolean).join(" ");
  const Row = ({ label, children }) => (
    <tr><td className="cs-lbl">{label}</td><td className="cs-val">{children}</td></tr>
  );
  return (
    <div className="motion-sheet">
      <div className="mo-caption">
        <div>IN THE {shared.courtType || c.law.court} FOR {dash(shared.county).toUpperCase()} COUNTY, {(shared.state || "").toUpperCase()}</div>
      </div>
      <h1 className="mo-title">CIVIL COVER SHEET</h1>
      <p style={{ textAlign: "center", fontStyle: "italic", marginTop: -8 }}>
        (Filed with SEUTRADE Financial Services' Motion for Temporary Injunction, Restraining Order, and Preservation of {c.CollateralNoun} Collateral)
      </p>
      <table className="cs-tab"><tbody>
        <Row label="Case No.">{dash(app.case_no || shared.caseNo)}</Row>
        <Row label="Plaintiff / Petitioner">{shared.plaintiff || "SEUTRADE FINANCIAL SERVICES, on behalf of the Borrower,"} (for {dash(c.borrower)})</Row>
        <Row label="Defendant / Respondent">{dash(c.lender)}</Row>
        <Row label="Nature of Suit">Injunctive Relief — Preservation of Collateral / Enforcement of Standstill Agreement</Row>
        <Row label="Cause of Action">{shared.causeOfAction || `Application for temporary restraining order and temporary injunction under ${c.law.statute}${c.isAuto ? `, and, where applicable, ${uccFor(shared.state)}` : ""}, to restrain enforcement pending resolution of a proposed refinancing/settlement.`}</Row>
        <Row label="Type of Collateral">{c.isAuto ? `Motor Vehicle — ${dash(vehLine)} (VIN ${dash(app.vehicle_vin)})` : `Real Property — ${dash(c.propertyAddr)}`}</Row>
        <Row label="Amount in Controversy">{money(c.bal)}{shared.amountNote ? ` — ${shared.amountNote}` : " (approximate balance of the underlying obligation)"}</Row>
        <Row label="Related Account / Loan No.">{dash(acctOf(app, shared))}</Row>
        {(app.treasury_doc_settings && (app.treasury_doc_settings.cusip || app.treasury_doc_settings.txnRef)) && (
          <Row label="Funding Source">Treasury Repo &amp; Collateral Financing (Fed Reserve MRA) · Ref {dash(app.treasury_doc_settings.txnRef)} · CUSIP {dash(app.treasury_doc_settings.cusip)}</Row>
        )}
        <Row label="PACER / CM-ECF Account No.">{FIRM.pacer}</Row>
        <Row label="Jury Demand">{shared.juryDemand || "No — equitable relief requested"}</Row>
        <Row label="Injunctive Relief Sought">Yes — Temporary Restraining Order and Temporary Injunction</Row>
        <Row label="Filing Party / Counsel">{dash(shared.repName)}{shared.repTitle ? `, ${shared.repTitle}` : ""}, SEUTRADE Financial Services</Row>
      </tbody></table>
      <p style={{ marginTop: 20 }}>The information contained herein neither replaces nor supplements the filing and service of pleadings or other papers as required by law or local rule. This form is submitted for the use of the Clerk of Court for the purpose of initiating the civil docket sheet.</p>
      <div className="mo-annot" data-testid="cs-statutory-annot">
        <strong>Statutory basis ({shared.state || "state"}):</strong> {c.law.statute}{c.isAuto ? `; ${uccFor(shared.state)} (UCC Article 9)` : ""}. Enforcement nature: {c.law.judicial}.
      </div>
      <div className="mo-sig">
        <p>Date: {fmtDate(shared.execDate)}</p>
        <p>Signature of Filing Party: <CursiveSig name={shared.repName ? `/s/ ${shared.repName}` : ""} /></p>
        <p>{dash(shared.repName)} — SEUTRADE FINANCIAL SERVICES · {dash(shared.repPhone)} · {dash(shared.repEmail)}</p>
      </div>
    </div>
  );
}

// ── Doc 3: Proposed Order ────────────────────────────────────────────────
function ProposedOrder({ app, shared }) {
  const c = ctx(app, shared);
  return (
    <div className="motion-sheet">
      <div className="mo-caption">
        <div>IN THE {shared.courtType || c.law.court} FOR {dash(shared.county).toUpperCase()} COUNTY, {(shared.state || "").toUpperCase()}</div>
        <div style={{ marginTop: 14 }}>{shared.plaintiff || "SEUTRADE FINANCIAL SERVICES, on behalf of the Borrower,"}</div>
        <div className="mo-i">Plaintiff/Petitioner,</div>
        <table className="mo-vtab"><tbody><tr>
          <td>v.</td>
          <td style={{ textAlign: "right" }}>Case No. {dash(app.case_no || shared.caseNo)}</td>
        </tr></tbody></table>
        <div>{dash(c.lender)},</div>
        <div className="mo-i">Defendant/Respondent.</div>
      </div>

      <h1 className="mo-title">[PROPOSED] ORDER GRANTING TEMPORARY INJUNCTION, RESTRAINING ORDER, AND PRESERVATION OF {c.CollateralNoun.toUpperCase()} COLLATERAL</h1>

      <p>THIS MATTER came before the Court upon the Motion of SEUTRADE Financial Services ("SEUTRADE"), on behalf of the Borrower, {dash(c.borrower)} ("Borrower"), for a temporary restraining order and temporary injunction preserving the {c.collateralNoun} identified below and enforcing the parties' standstill arrangement pending resolution of the proposed refinancing, payoff, or settlement of the obligation held by Defendant/Respondent, {dash(c.lender)} ("Lender").</p>
      <p>The Court, having reviewed the Motion, the supporting materials, and the applicable law ({c.law.statute}{c.isAuto ? `, and, where applicable, ${uccFor(shared.state)}` : ""}), and being otherwise fully advised, FINDS that immediate and irreparable injury may result to the Borrower absent the relief requested, that the balance of hardships favors preservation of the status quo, and that entry of this Order is just and proper.</p>
      <div className="mo-annot" data-testid="ord-statutory-annot">
        <strong>Controlling authority ({shared.state || "state"}):</strong> {c.law.statute}{c.isAuto ? `; ${uccFor(shared.state)} (UCC Article 9)` : ""}. Enforcement nature: {c.law.judicial}.
      </div>
      <p style={{ fontWeight: 700, marginTop: 14 }}>IT IS THEREFORE ORDERED, ADJUDGED, AND DECREED that:</p>
      <ol className="mo-ol">
        <li>The Motion is GRANTED.</li>
        <li>Defendant/Respondent {dash(c.lender)}, and its agents, {c.isAuto ? "repossession contractors" : "foreclosure trustees and sale agents"}, employees, attorneys, representatives, assigns, and all persons acting in concert with it, are hereby RESTRAINED and ENJOINED from {c.enforceVerbs} the {c.collateralNoun} described as: {c.isAuto ? `${dash([app.vehicle_year, app.vehicle_make, app.vehicle_model].filter(Boolean).join(" "))}, VIN ${dash(app.vehicle_vin)}` : dash(c.propertyAddr)} (Loan/Account No. {dash(acctOf(app, shared))}).</li>
        <li>The Lender shall PRESERVE the {c.collateralNoun} and shall not sell, transfer, assign, dispose of, or otherwise change its status during the pendency of this Order.</li>
        <li>The Lender shall, within {dash(shared.payoffDays || "ten (10)")} days, provide SEUTRADE and/or the Borrower with a current payoff, reinstatement, or account statement.</li>
        <li>This Order shall remain in effect until {dash(shared.orderExpiry || "further order of this Court")}, or until a hearing on the application for temporary injunction, currently set for {dash(shared.hearingDate)} at {dash(shared.hearingTime)}.</li>
        <li>Bond/security is {shared.bondWaived ? "WAIVED" : `set in the amount of ${money(shared.bondAmount)}`}.</li>
        <li>The Court retains jurisdiction to enforce, modify, or dissolve this Order.</li>
      </ol>

      <div className="mo-sig" style={{ marginTop: 26 }}>
        <p>SO ORDERED this _____ day of ________________, 20____.</p>
        <p style={{ marginTop: 22 }}>______________________________</p>
        <p>JUDGE, {shared.courtType || c.law.court}</p>
        <p>{shared.judgeName ? `Hon. ${shared.judgeName}` : ""}</p>
      </div>
    </div>
  );
}

// ── Doc 4: Certificate of Service (standalone) ───────────────────────────
function CertificateOfService({ app, shared }) {
  const c = ctx(app, shared);
  return (
    <div className="motion-sheet">
      <div className="mo-caption">
        <div>IN THE {shared.courtType || c.law.court} FOR {dash(shared.county).toUpperCase()} COUNTY, {(shared.state || "").toUpperCase()}</div>
        <div style={{ marginTop: 14 }}>{shared.plaintiff || "SEUTRADE FINANCIAL SERVICES, on behalf of the Borrower,"}</div>
        <div className="mo-i">Plaintiff/Petitioner,</div>
        <table className="mo-vtab"><tbody><tr>
          <td>v.</td>
          <td style={{ textAlign: "right" }}>Case No. {dash(app.case_no || shared.caseNo)}</td>
        </tr></tbody></table>
        <div>{dash(c.lender)},</div>
        <div className="mo-i">Defendant/Respondent.</div>
      </div>

      <h1 className="mo-title">CERTIFICATE OF SERVICE</h1>

      <p>I HEREBY CERTIFY that a true and correct copy of the foregoing (i) Civil Cover Sheet, (ii) Motion for Temporary Injunction, Restraining Order, Enforcement of Standstill Agreement, and Preservation of {c.CollateralNoun} Collateral, and (iii) [Proposed] Order was served upon the following party/counsel of record by {shared.serviceMethod || "[ELECTRONIC SERVICE / CERTIFIED MAIL / PERSONAL SERVICE / OTHER AUTHORIZED METHOD]"} on {execLine(shared.serviceDate || shared.execDate)}:</p>

      <div className="mo-block">
        {dash(c.lender)} {shared.lenderCounsel ? `(c/o ${shared.lenderCounsel})` : ""}<br />
        {dash(app.current_lender_address || shared.lenderServiceAddress)}<br />
        {dash(shared.lenderServiceEmail)}
      </div>

      <p>I declare under penalty of perjury under the laws of the State of {shared.state || "[STATE]"} that the foregoing is true and correct.</p>

      <div className="mo-sig">
        <p><CursiveSig name={shared.repName ? `/s/ ${shared.repName}` : ""} /></p>
        <p>{dash(shared.repName)}</p>
        <p>{dash(shared.repTitle)}</p>
        <p>SEUTRADE FINANCIAL SERVICES</p>
        <p>{dash(shared.repAddress)}</p>
        <p>{dash(shared.repPhone)} · {dash(shared.repEmail)}</p>
      </div>
    </div>
  );
}

const DocDivider = ({ n, title }) => (
  <div className="pk-divider no-screen-hide">
    <span className="pk-tab">TAB {n}</span> {title}
  </div>
);

// The full 4-document packet for one borrower.
export const PK_CSS = `
        .paper-sheet{background:#fff;color:#141410;max-width:8.5in;margin:0 auto 28px;padding:0.9in 0.85in;box-shadow:0 10px 40px rgba(0,0,0,.4);font-family:'Times New Roman',Georgia,serif;font-size:12.5pt;line-height:1.55;}
        .pk-divider{font-family:'Times New Roman',Georgia,serif;font-size:10.5pt;font-weight:700;color:#5C4A16;text-transform:uppercase;letter-spacing:.06em;border-bottom:2px solid #C9A227;padding-bottom:6px;margin:-0.2in 0 0.35in;display:flex;align-items:center;gap:10px;}
        .pk-tab{background:#C9A227;color:#0A0A0A;padding:2px 8px;border-radius:3px;font-size:9pt;}
        .mo-caption{text-align:center;font-weight:700;}
        .mo-caption .mo-i{font-style:italic;font-weight:400;}
        .mo-vtab{width:100%;margin:6px 0;} .mo-vtab td{font-weight:400;}
        .mo-title{font-size:13pt;font-weight:700;text-align:center;margin:22px 0 18px;text-transform:uppercase;line-height:1.4;}
        .mo-h{font-size:12.5pt;font-weight:700;margin:18px 0 8px;}
        .mo-ol{margin:0 0 8px 0;padding-left:26px;} .mo-ol li{margin-bottom:8px;text-align:justify;}
        .mo-alpha{list-style:upper-alpha;}
        .mo-block{margin:8px 0 8px 18px;font-weight:600;}
        .mo-annot{margin:12px 0;padding:9px 12px;border-left:3px solid #B8860B;background:#faf6ea;font-size:10.5pt;line-height:1.45;color:#3a3320;}
        .mo-sig{margin:16px 0;} .mo-sig p{margin:2px 0;}
        .cs-tab{width:100%;border-collapse:collapse;margin-top:14px;}
        .cs-tab td{border:1px solid #333;padding:7px 9px;vertical-align:top;}
        .cs-lbl{width:34%;font-weight:700;background:#f4f0e4;font-size:11pt;}
        .cs-val{font-size:11.5pt;}
        p{text-align:justify;margin:8px 0;}
        @media print{
          body{background:#fff!important;}
          .no-print{display:none!important;}
          .cm-print-area{padding:0!important;max-width:none!important;}
          .paper-sheet{box-shadow:none!important;margin:0!important;max-width:none!important;padding:0.6in!important;page-break-after:always;}
          @page{size:letter;margin:0.5in;}
        }
`;


export function Packet({ app, shared, idx, last }) {
  return (
    <>
      <div className="paper-sheet" data-testid={`pk-cover-${app.id}`}>
        <DocDivider n="1" title="Civil Cover Sheet" />
        <CivilCoverSheet app={app} shared={shared} />
      </div>
      <div className="paper-sheet" data-testid={`pk-motion-${app.id}`}>
        <DocDivider n="2" title="Motion for Temporary Injunction & Preservation of Collateral" />
        <Motion app={app} shared={shared} />
      </div>
      <div className="paper-sheet" data-testid={`pk-order-${app.id}`}>
        <DocDivider n="3" title="[Proposed] Order" />
        <ProposedOrder app={app} shared={shared} />
      </div>
      <div className="paper-sheet" data-testid={`pk-cos-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
        <DocDivider n="4" title="Certificate of Service" />
        <CertificateOfService app={app} shared={shared} />
      </div>
    </>
  );
}

export default function MotionPacket() {
  const { appId } = useParams();
  const q = useQuery();
  const idsParam = q.get("ids");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shared, setShared] = useState({
    state: "Tennessee", county: "", courtType: "", caseNo: "",
    plaintiff: "SEUTRADE FINANCIAL SERVICES, on behalf of the Borrower,",
    standstillDate: "", receivedDate: "", deliveryEvidence: "email delivery confirmation and/or certified mail receipt",
    repName: FIRM.officer, repTitle: "Authorized Representative", repAddress: FIRM.address,
    repPhone: "", repEmail: "seutradeinfo@gmail.com", lenderCounsel: "", lenderServiceAddress: "", lenderServiceEmail: "",
    serviceMethod: "", borrowerAddress: "", execDate: todayISO(), serviceDate: todayISO(),
    // Packet-specific
    causeOfAction: "", amountNote: "", juryDemand: "No — equitable relief requested",
    hearingDate: "", hearingTime: "", judgeName: "", payoffDays: "ten (10)",
    orderExpiry: "", bondWaived: false, bondAmount: 0,
    acct: {},
  });
  const set = (k) => (v) => setShared((p) => ({ ...p, [k]: v }));
  const setAcct = (id) => (v) => setShared((p) => ({ ...p, acct: { ...p.acct, [id]: v } }));

  useEffect(() => {
    const ids = idsParam ? idsParam.split(",").map((s) => s.trim()).filter(Boolean) : (appId ? [appId] : []);
    if (!ids.length) { setLoading(false); return; }
    (async () => {
      const token = localStorage.getItem("th_admin_token");
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const results = [];
      for (const id of ids) {
        try {
          const r = await axios.get(`${API}/borrower/applications/${id}`, cfg);
          if (r.data.application) results.push(r.data.application);
        } catch { /* skip */ }
      }
      if (results[0]) {
        const guess = STATE_NAMES.find((s) => (results[0].property_or_collateral || "").toLowerCase().includes(s.toLowerCase()));
        const savedSettings = (results.find((a) => a.motion_doc_settings) || {}).motion_doc_settings || null;
        const acctMap = {};
        results.forEach((a) => { const v = a.loan_account_no || a.account_no || a.loan_number || ""; if (v) acctMap[a.id] = v; });
        setShared((p) => ({
          ...p,
          ...(guess ? { state: guess } : {}),
          ...(savedSettings || {}),
          acct: { ...acctMap, ...(savedSettings && savedSettings.acct ? savedSettings.acct : {}), ...p.acct },
        }));
      }
      setApps(results);
      setLoading(false);
    })();
  }, [appId, idsParam]);

  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const saveDocData = async () => {
    if (!apps.length) return;
    const token = localStorage.getItem("th_admin_token");
    const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    const { acct, ...settings } = shared;
    setSaving(true); setSavedMsg("");
    try {
      await Promise.all(apps.map((a) => axios.post(`${API}/admin/borrower-applications/${a.id}/motion-data`,
        { loan_account_no: (shared.acct && shared.acct[a.id] !== undefined) ? shared.acct[a.id] : acctOf(a, shared), motion_doc_settings: settings }, cfg)));
      setSavedMsg("Saved to file ✓");
      setTimeout(() => setSavedMsg(""), 4000);
    } catch (e) {
      setSavedMsg("Save failed");
      setTimeout(() => setSavedMsg(""), 4000);
    } finally { setSaving(false); }
  };

  const law = forState(shared.state);

  return (
    <div style={{ background: "#0A0A0A", minHeight: "100vh" }}>
      <div className="no-print" style={{ position: "sticky", top: 0, zIndex: 20, background: "#0F0F0A", borderBottom: "1px solid #2A2A22" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "14px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <Link to="/workbench" style={{ color: "#8A8A8A", display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}><ArrowLeft size={14} /> Back</Link>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#E4C97A", fontWeight: 700, letterSpacing: ".08em", fontSize: 13 }}>
              <FolderKanban size={16} /> COURT-READY MOTION PACKET {apps.length > 1 ? `· BULK (${apps.length})` : ""}
            </div>
            <button onClick={saveDocData} disabled={saving || !apps.length} data-testid="pk-save" style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color: "#E4C97A", border: "1px solid #B8860B", padding: "9px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
              <Save size={14} /> {saving ? "Saving…" : "Save to file"}
            </button>
            {savedMsg && <span data-testid="pk-saved-msg" style={{ color: savedMsg.includes("fail") ? "#FF8585" : "#3DBB8E", fontSize: 11, fontWeight: 700 }}>{savedMsg}</span>}
            <CourtEfaxButton appId={appId} motionType="packet" />

            <button onClick={() => window.print()} data-testid="pk-print" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#C9A227", color: "#0A0A0A", border: "none", padding: "9px 16px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", cursor: "pointer" }}>
              <Printer size={14} /> Print / Save packet as PDF
            </button>
          </div>
          <div className="text-[11px] text-[#9DB4E8] mb-2" data-testid="pk-contents">
            Each borrower's packet = <strong>Civil Cover Sheet → Motion → [Proposed] Order → Certificate of Service</strong>, in one court-ready PDF for e-filing.
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">State (sets court &amp; law)</label>
              <select value={shared.state} onChange={(e) => set("state")(e.target.value)} data-testid="pk-state"
                className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]">
                {STATE_NAMES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <Ctl label="County" value={shared.county} onChange={set("county")} />
            <Ctl label={`Court (default: ${law.court})`} value={shared.courtType} onChange={set("courtType")} />
            <Ctl label="Case No. (single)" value={shared.caseNo} onChange={set("caseNo")} />
            <Ctl label="Standstill sent (date)" value={shared.standstillDate} onChange={set("standstillDate")} type="date" />
            <Ctl label="Lender received (date)" value={shared.receivedDate} onChange={set("receivedDate")} type="date" />
            <Ctl label="Authorized representative" value={shared.repName} onChange={set("repName")} />
            <Ctl label="Rep. title" value={shared.repTitle} onChange={set("repTitle")} />
            <Ctl label="Rep. phone" value={shared.repPhone} onChange={set("repPhone")} />
            <Ctl label="Rep. email" value={shared.repEmail} onChange={set("repEmail")} />
            <Ctl label="Lender counsel (optional)" value={shared.lenderCounsel} onChange={set("lenderCounsel")} />
            <Ctl label="Lender service address" value={shared.lenderServiceAddress} onChange={set("lenderServiceAddress")} />
            <Ctl label="Lender service email" value={shared.lenderServiceEmail} onChange={set("lenderServiceEmail")} />
            <Ctl label="Service method" value={shared.serviceMethod} onChange={set("serviceMethod")} />
            <Ctl label="Execution / signature date" value={shared.execDate} onChange={set("execDate")} type="date" />
            <Ctl label="Service date" value={shared.serviceDate} onChange={set("serviceDate")} type="date" />
            <Ctl label="Hearing date (order)" value={shared.hearingDate} onChange={set("hearingDate")} type="date" />
            <Ctl label="Hearing time (order)" value={shared.hearingTime} onChange={set("hearingTime")} />
            <Ctl label="Judge (order)" value={shared.judgeName} onChange={set("judgeName")} />
            <Ctl label="Payoff turnaround (days)" value={shared.payoffDays} onChange={set("payoffDays")} />
            <div className="flex items-center gap-2 md:col-span-2">
              <label className="flex items-center gap-2 text-[12px] text-[#ECECE4] cursor-pointer">
                <input type="checkbox" checked={shared.bondWaived} onChange={(e) => set("bondWaived")(e.target.checked)} data-testid="pk-bond-waived" />
                Waive bond in the proposed order
              </label>
              {!shared.bondWaived && (
                <input type="number" placeholder="Bond amount" value={shared.bondAmount || ""} onChange={(e) => set("bondAmount")(Number(e.target.value))} data-testid="pk-bond-amount"
                  className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-1.5 text-[13px] text-[#ECECE4] w-36" />
              )}
            </div>
          </div>
          {apps.length > 0 && (
            <div className="mt-3 border-t border-[#2A2A22] pt-3" data-testid="pk-acct-editor">
              <div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-2">Loan / Account No. {apps.length > 1 ? "(per borrower)" : ""} — populates every document in the packet</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {apps.map((a) => (
                  <div key={a.id} className="flex items-center gap-2">
                    <span className="text-[11px] text-[#9A9A90] min-w-[120px] truncate">{a.borrower_full_name || a.borrower_name || a.id}</span>
                    <input value={acctOf(a, shared)} onChange={(e) => setAcct(a.id)(e.target.value)} data-testid={`pk-acct-${a.id}`}
                      placeholder="Enter loan / account no."
                      className="flex-1 bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-[10.5px] text-[#8A8A8A] mt-2">Law auto-set for <strong className="text-[#E4C97A]">{shared.state}</strong>: {law.statute}. Fields left blank print as court-fillable blanks. Citations are best-effort — counsel should verify venue &amp; authority before filing.</div>
        </div>
      </div>

      <div className="cm-print-area" style={{ maxWidth: 900, margin: "0 auto", padding: "28px 0" }}>
        {loading ? (
          <div style={{ color: "#8A8A8A", textAlign: "center", padding: 60 }}>Loading…</div>
        ) : apps.length === 0 ? (
          <div style={{ color: "#8A8A8A", textAlign: "center", padding: 60 }} data-testid="pk-empty">No application loaded. Open this packet from a borrower file, or pass ?ids=… for bulk.</div>
        ) : (
          apps.map((app, i) => (
            <Packet key={app.id} app={app} shared={shared} idx={i} last={i === apps.length - 1} />
          ))
        )}
      </div>

      <style>{`
        .paper-sheet{background:#fff;color:#141410;max-width:8.5in;margin:0 auto 28px;padding:0.9in 0.85in;box-shadow:0 10px 40px rgba(0,0,0,.4);font-family:'Times New Roman',Georgia,serif;font-size:12.5pt;line-height:1.55;}
        .pk-divider{font-family:'Times New Roman',Georgia,serif;font-size:10.5pt;font-weight:700;color:#5C4A16;text-transform:uppercase;letter-spacing:.06em;border-bottom:2px solid #C9A227;padding-bottom:6px;margin:-0.2in 0 0.35in;display:flex;align-items:center;gap:10px;}
        .pk-tab{background:#C9A227;color:#0A0A0A;padding:2px 8px;border-radius:3px;font-size:9pt;}
        .mo-caption{text-align:center;font-weight:700;}
        .mo-caption .mo-i{font-style:italic;font-weight:400;}
        .mo-vtab{width:100%;margin:6px 0;} .mo-vtab td{font-weight:400;}
        .mo-title{font-size:13pt;font-weight:700;text-align:center;margin:22px 0 18px;text-transform:uppercase;line-height:1.4;}
        .mo-h{font-size:12.5pt;font-weight:700;margin:18px 0 8px;}
        .mo-ol{margin:0 0 8px 0;padding-left:26px;} .mo-ol li{margin-bottom:8px;text-align:justify;}
        .mo-alpha{list-style:upper-alpha;}
        .mo-block{margin:8px 0 8px 18px;font-weight:600;}
        .mo-annot{margin:12px 0;padding:9px 12px;border-left:3px solid #B8860B;background:#faf6ea;font-size:10.5pt;line-height:1.45;color:#3a3320;}
        .mo-sig{margin:16px 0;} .mo-sig p{margin:2px 0;}
        .cs-tab{width:100%;border-collapse:collapse;margin-top:14px;}
        .cs-tab td{border:1px solid #333;padding:7px 9px;vertical-align:top;}
        .cs-lbl{width:34%;font-weight:700;background:#f4f0e4;font-size:11pt;}
        .cs-val{font-size:11.5pt;}
        p{text-align:justify;margin:8px 0;}
        @media print{
          body{background:#fff!important;}
          .no-print{display:none!important;}
          .cm-print-area{padding:0!important;max-width:none!important;}
          .paper-sheet{box-shadow:none!important;margin:0!important;max-width:none!important;padding:0.6in!important;page-break-after:always;}
          @page{size:letter;margin:0.5in;}
        }
      `}</style>
    </div>
  );
}
